import { useSearchAndShuffleContext } from '../../context';
import { TakenCard, AvailableCard, SavedCardData } from '../../types';

type useSavedCardActionsReturn = {
  saveOnYourJobs: (
    card: TakenCard | AvailableCard,
    searchAlertId?: string
  ) => void;
  removeSavedCard: (cardId: string) => void;
  updateYourJobsWithAlertId: (cardId: string, searchAlertId: string) => void;
};

export const useSavedCardActions = (): useSavedCardActionsReturn => {
  const { savedCards, setSavedCards, setDeletingSavedCards } =
    useSearchAndShuffleContext();

  const saveOnYourJobs = (
    card: TakenCard | AvailableCard,
    searchAlertId?: string
  ): void => {
    if (!card.data.id || !card.data.jobTitle) {
      return;
    }

    const existingIndex = savedCards.findIndex(
      (savedCard) => savedCard.data.id === card.data.id
    );

    // Card already exists in saved cards
    if (existingIndex !== -1) {
      const updatedCard = {
        ...savedCards[existingIndex],
        updatedAt: new Date(),
      };
      setSavedCards((prev) => [
        updatedCard,
        ...prev.filter((_, idx) => idx !== existingIndex),
      ]);
      return;
    }

    const savedCard: SavedCardData = {
      type: card.type,
      updatedAt: new Date(),
      data: {
        id: card.data.id,
        jobSlug: card.data.jobSlug,
        title: card.data.jobTitle || '',
        companyName:
          card.data.company?.companyName ||
          card.data.fallbackCompany?.companyName ||
          '',
        companyLogoUrl: card.data.company?.logos?.logo256px || null,
        location: card.data.location || '',
        urn: card.data.urn || '',
        ...(card.type === 'TakenJob' ? { searchAlertId: searchAlertId } : {}),
      },
    };

    setSavedCards((prev) => [savedCard, ...prev]);
  };

  const updateYourJobsWithAlertId = (
    cardId: string,
    searchAlertId: string
  ): void => {
    setSavedCards((prev) =>
      prev.map((card) =>
        card.data.id === cardId
          ? { ...card, data: { ...card.data, searchAlertId } }
          : card
      )
    );
  };

  const removeSavedCard = (cardId: string): void => {
    setDeletingSavedCards((prev) => [...prev, cardId]);
  };

  return {
    saveOnYourJobs,
    updateYourJobsWithAlertId,
    removeSavedCard,
  };
};
